import Button from '@atlaskit/button/new';
import PageHeader from '@atlaskit/page-header';
import { Box, Inline, Stack, xcss } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import { GlobalPageLoadExperience } from '@atlassian/ufo';
import { DatabaseContainerType, useAnalytics } from '@databases/analytics';
import { useLocale } from '@databases/intl';
import { messages } from '@databases/intl/messages';
import DatabaseResizeObserver from '@databases/layout/observer/ResizeObserver';
import { logError } from '@databases/logs';
import { isNativeEmbedEditorPlugin } from '@databases/modes';
import { useCallbackEffect } from '@databases/react';
import { AsyncOperationStatus } from '@databases/redux-types';
import { appStoreUtils } from '@databases/redux/store';
import {
  getDatabaseEntryOptional,
  getEntryName,
  getFirstField,
  isBrokenDatabase
} from '@databases/utils/database/utils';
import { loadDatabaseByContentId } from '@databases/utils/redux/databases/actions';
import {
  selectDatabaseByContentIdOptional,
  selectDatabaseByContentIdStatus
} from '@databases/utils/redux/databases/selectors';
import { deserializeError } from '@databases/utils/utils';
import React, { lazy, type FunctionComponent, Suspense, useCallback } from 'react';
import { useIntl } from 'react-intl-next';

import { getExperimentValue } from '@canvas/client-feature-flags';
import { dynamicImport } from '@canvas/dynamic-import';

import genericErrorIllustration from '../../../../assets/generic_error.svg';
import DatabaseEntry, {
  type DatabaseEntryPresentation
} from '../../../../common/components/database-entry/DatabaseEntry.tsx';
import DatabaseEntrySkeleton from '../../../../common/components/database-entry/DatabaseEntrySkeleton.tsx';
import DatabaseEntryTitle from '../../../../common/components/database-entry/DatabaseEntryTitle.tsx';
import ImmediateEntryActions, {
  type ImmediateEntryActionKey
} from '../../../../common/components/database-entry/ImmediateEntryActions.tsx';
import { ButtonGroup } from '../../../../common/components/styled.tsx';

const ShrinkingEmptyState = lazy(() =>
  dynamicImport(() => import('../../../../common/components/ShrinkingEmptyState.tsx'))
);

const EmbedEmptyState = lazy(() => dynamicImport(() => import('./EmbedEmptyState.tsx')));

const entryActions: ImmediateEntryActionKey[] = ['print'];

const entryScrollStyles = xcss({ overflowY: 'auto', height: '100%' });
const redesignedActionsStyles = xcss({
  paddingBlock: 'space.200',
  paddingInline: 'space.300'
});
const redesignedEntryStyles = xcss({
  paddingBlock: 'space.300',
  paddingInline: 'space.300'
});

const EmbedEntryDetails: FunctionComponent<{
  contentId: string;
  entryId: string;
  isContentEditable?: boolean;
  onDataLoaded?: () => void;
}> = ({ contentId, entryId, isContentEditable = true, onDataLoaded }) => {
  const [databaseExists, entryExists, title, titleField] = appStoreUtils.useEqualSelector(
    (state) => {
      const database = appStoreUtils.useSelector((state) =>
        selectDatabaseByContentIdOptional(state, { contentId })
      );
      const entry =
        database &&
        !isBrokenDatabase(database) &&
        getDatabaseEntryOptional(database, entryId);
      return [
        !!database,
        !!entry,
        database && entry ? getEntryName({ database, entry }, state) : undefined,
        database && !isBrokenDatabase(database) ? getFirstField(database) : undefined
      ] as const;
    }
  );

  const intl = useIntl();
  const { formatMessage } = intl;

  const dispatch = appStoreUtils.useDispatch();
  useCallbackEffect(
    useCallback(async () => {
      GlobalPageLoadExperience.mark('database-load-start');
      await dispatch(loadDatabaseByContentId(contentId));
    }, [contentId, dispatch])
  );
  const databaseStatus = appStoreUtils.useSelector((state) =>
    selectDatabaseByContentIdStatus(state, { contentId })
  );

  const isNativeEmbedsV2 =
    isNativeEmbedEditorPlugin() &&
    getExperimentValue('mauiExperiment', 'isEnabled', {
      fireExperimentExposure: false
    }) &&
    getExperimentValue('dbNativeEmbedsV2', 'isEnabled', {
      fireExperimentExposure: true
    });

  const client = useAnalytics();
  const locale = useLocale();

  useCallbackEffect(
    useCallback(async () => {
      await client.sendDatabaseContentViewedEvent({
        contentId,
        databaseContainer: DatabaseContainerType.EMBED_ENTRY,
        locale
      });
    }, [client, contentId, locale])
  );

  switch (databaseStatus?.status) {
    case AsyncOperationStatus.ERROR: {
      const error = deserializeError(databaseStatus.error);
      const formattedError = {
        name: error.name,
        message: error.message,
        stack: error.stack
      };
      GlobalPageLoadExperience.failure({
        metadata: { error: formattedError },
        force: true
      });
      logError(new Error(`Couldn't load link with error: ${error}`));
      return (
        <Suspense fallback={<Spinner interactionName="load" />}>
          <ShrinkingEmptyState
            header={formatMessage(messages['embed-view.error-heading-text'])}
            description={formatMessage(messages['embed-view.error-description-text'])}
            imageUrl={genericErrorIllustration}
            primaryAction={
              <Button appearance="primary" onClick={() => globalThis.location.reload()}>
                {formatMessage(messages['embed-view.try-again-button-text'])}
              </Button>
            }
            maxImageWidth={300}
            maxImageHeight={250}
          />
        </Suspense>
      );
    }
    case AsyncOperationStatus.SUCCEEDED:
      GlobalPageLoadExperience.mark('database-load-end');
      GlobalPageLoadExperience.success({ force: true });
      onDataLoaded?.();
      break;
    default:
      return <DatabaseEntrySkeleton />;
  }

  if (!databaseExists) {
    return (
      <Suspense fallback={<Spinner interactionName="load" />}>
        <EmbedEmptyState />
      </Suspense>
    );
  }

  if (!entryExists) {
    return (
      <Suspense fallback={<Spinner interactionName="load" />}>
        <EmbedEmptyState />
      </Suspense>
    );
  }

  const isEntryDetailsRedesign = getExperimentValue(
    'databasesFrontendEntryDetailsRedesign',
    'isEnabled',
    { fireExperimentExposure: true }
  );

  const presentation: DatabaseEntryPresentation = isEntryDetailsRedesign
    ? { kind: 'entry-details-redesign' }
    : isNativeEmbedsV2
      ? { kind: 'native-embed-v2', hideFirstVisibleField: false }
      : { kind: 'default' };

  const actions = (
    <ButtonGroup>
      <ImmediateEntryActions
        contentId={contentId}
        entryId={entryId}
        placement="bottom-end"
        show={entryActions}
      />
    </ButtonGroup>
  );

  const entryContent = (
    <DatabaseEntry
      contentId={contentId}
      entryId={entryId}
      isContentEditable={isContentEditable}
      isEmbedEntryView
      presentation={presentation}
      emptyValueLabel={
        isNativeEmbedsV2
          ? formatMessage(messages['embed-view.empty-entry-label'])
          : undefined
      }
    />
  );

  return (
    <>
      <Box id="layout-toolbar-wrapper">
        {isEntryDetailsRedesign ? (
          <Inline alignInline="end" xcss={redesignedActionsStyles}>
            {actions}
          </Inline>
        ) : (
          <PageHeader id="embed-entry-details-entry-name" actions={actions}>
            {title ? title : formatMessage(messages['unnamed-entities.unnamed-entry'])}
          </PageHeader>
        )}
      </Box>

      <Box xcss={entryScrollStyles}>
        <DatabaseResizeObserver disableResize={isNativeEmbedEditorPlugin()}>
          {isEntryDetailsRedesign ? (
            <Stack space="space.300" xcss={redesignedEntryStyles}>
              <DatabaseEntryTitle
                title={title}
                titleField={titleField}
                headingLevel="h1"
              />
              {entryContent}
            </Stack>
          ) : (
            entryContent
          )}
        </DatabaseResizeObserver>
      </Box>
    </>
  );
};
export default EmbedEntryDetails;
